'use client'

import { memo, useEffect, useMemo } from 'react'

import { isLatitudeAndLongitudeError } from 'modules/customer'
import { useDeliverySupport } from 'modules/delivery'
import { NavigationOpenables } from 'shared/types/navigation'
import { noop } from 'shared/utils/common'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import useCartStore from '../../../store/cartStore'
import CartReservationLabel from '../components/CartReservationLabel'
import CartReservationVerifyAddress from '../components/CartReservationVerifyAddress'
import TmsDeliveryHeader from '../components/TmsDeliveryHeader'
import TmsDeliverySelection from '../components/TmsDeliverySelection'
import { useTmsReservationHandlers } from '../hooks/useCancelDeliveryReservation'

interface CartDeliverySlotSelectionViewProps {
  isInDropdown?: boolean
  setOpenDropdown?: (name: NavigationOpenables, value: boolean) => void
}

const CartDeliverySlotSelectionView: React.FC<CartDeliverySlotSelectionViewProps> = ({
  isInDropdown = false,
  setOpenDropdown
}) => {
  const { isEditing, handlers } = useTmsReservationHandlers(setOpenDropdown)
  const { cart, refreshCart, isLoading } = useCartStore()
  const { isPostalCodeAndCitySupported } = useDeliverySupport()

  const hasReservation = useMemo(
    () => isNotNilOrEmpty(cart?.tmsReservationSlotData),
    [cart?.tmsReservationSlotData]
  )

  const hasCoordinates = useMemo(
    () =>
      !isLatitudeAndLongitudeError(
        cart?.deliveryAddress?.latitude,
        cart?.deliveryAddress?.longitude
      ),
    [cart?.deliveryAddress?.latitude, cart?.deliveryAddress?.longitude]
  )

  const showReservationSelection = useMemo(
    () => !hasReservation || isEditing,
    [hasReservation, isEditing]
  )

  useEffect(() => {
    refreshCart().then(noop).catch(noop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <TmsDeliveryHeader
        hasReservation={hasReservation}
        isEditing={isEditing}
        onCancelEdit={handlers.handleCancelEdit}
      />
      {showReservationSelection ? (
        <TmsDeliverySelection
          isInDropdown={isInDropdown}
          hasCoordinates={hasCoordinates}
          reservationSlotData={cart?.tmsReservationSlotData || undefined}
          onEndReservation={handlers.handleEndReservation}
          isPostalCodeAndCitySupported={isPostalCodeAndCitySupported}
        />
      ) : (
        cart?.tmsReservationSlotData && (
          <CartReservationLabel
            reservationSlotData={cart.tmsReservationSlotData}
            handleEditReservation={handlers.handleEdit}
            handleCancelReservation={handlers.handleCancelReservation}
            cancelLoading={isLoading}
          />
        )
      )}
      <CartReservationVerifyAddress />
    </>
  )
}

export default memo(CartDeliverySlotSelectionView)
